import {ChangeEvent, FC, useEffect, useMemo, useRef, useState} from 'react';
import {useCan} from 'src/permissions/hooks';
import {Box, Button, OutlinedInput, Stack, Tooltip} from '@mui/material';
import {DoneAllOutlined, NotificationsNoneOutlined, Search} from '@mui/icons-material';
import {FormattedMessage, useIntl} from 'react-intl';
import {EmptyStatus} from '../../components/notifications/EmptyStatus';
import {NotificationCard} from '../../components/notifications/NotificationCard';
import {notificationApi} from '../../api';
import {NotificationsSortEnum} from '../../types';
import {isErrorObject} from '../../utils/helper';
import {NotifyError, NotifySuccess} from '../../utils/notification';
import {store} from '../../app/store';
import {preloaderChangeStatus} from '../../stores/common/commonSlice';
import {useBusinessId} from '../../app/hooks';
import {TableLayout} from '../../components/tables/TableLayout';
import {FilterBar} from '../../components/common/FilterBar';
import {searchInputSx, toolbarButtonSx} from '../../components/tables/tableToolbar.sx';
import {
    NotificationStatsStrip,
    NotificationStatus,
    readFlagOf,
} from '../../components/notifications/NotificationStatsStrip';
import {useListQueryState} from 'src/hooks/useListQueryState';

/** Long enough that a keystroke does not cost a request, short enough to feel immediate. */
const SEARCH_DELAY_MS = 350;

const DEFAULT_PAGE_SIZE = 100;

/**
 * Everything this screen can be looking at, and the only copy of it.
 *
 * The page number was already in the query string, but the read filter and the search were in
 * `useState` - so opening a notification and coming back landed on an unfiltered first page. The
 * page itself was read back through `location.state` or a regex over `location.search`, which the
 * hook now does by name.
 */
const QUERY_DEFAULTS = {
    page: 0,
    size: DEFAULT_PAGE_SIZE,
    keyword: '',
    status: 'all' as string,
};

type GetNotificationTriggerType = ReturnType<
    typeof notificationApi.useLazyGetNotificationsQuery
>[0];

const handleGetNotifications = (
    trigger: GetNotificationTriggerType,
    businessId: number,
    pageSize: number,
    page: number,
    filters: {read?: boolean; keyword?: string}
) => {
    try {
        store.dispatch(preloaderChangeStatus(true));
        trigger({
            params: {
                size: pageSize,
                page,
            },
            // Every type, to everyone the endpoint lets in. The list used to be derived from the
            // account type, so a manager simply never saw the kinds their type was not on - a message
            // nobody was told about is a message lost, and who may read notifications at all is a
            // question notifications.search already answers.
            data: {
                businessId,
                sortField: NotificationsSortEnum.CREATED,
                ...filters,
            },
        });
    } catch (error) {
        if (isErrorObject(error)) {
            NotifyError(error.data.message);
        }
    } finally {
        store.dispatch(preloaderChangeStatus(false));
    }
};

export const Notifications: FC = () => {
    // The business being worked in: the caller's own, or the one a platform administrator picked.
    // Reading `authInfo.business.id` directly was a crash for them - they have no business of
    // their own, and null has no id.
    const businessId = useBusinessId();
    const intl = useIntl();
    const [getNotifications, notificationsData] = notificationApi.useLazyGetNotificationsQuery();
    const [markAsRead] = notificationApi.useMarkNotificationAsReadMutation();
    const [query, setQuery] = useListQueryState(QUERY_DEFAULTS);
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const status = query.status as NotificationStatus;
    const search = query.keyword;
    // The input is controlled and waits for a pause in the typing, so its text is local while the URL
    // holds what has actually been applied. Reading the box straight from the address bar would type
    // a character behind. Seeded from the URL, which is what a reload or a Back hands over.
    const [keyword, setKeyword] = useState(query.keyword);
    // Same body as the list, without the read flag the tiles set - so the counts are the list's, the
    // search included.
    const {data: notificationStats} = notificationApi.useGetNotificationStatsQuery({
        businessId,
        keyword: search || undefined,
    });

    // A tile narrows the list; the chosen one clicked again, or All, widens it back. A new filter is a
    // new result set: its first page, and nothing selected from the old one.
    const handleStatusChange = (next: NotificationStatus) => {
        setSelectedIds(new Set());
        setQuery({...query, page: 0, status: status === next ? 'all' : next});
    };

    // The search runs once typing pauses, not on every keystroke.
    useEffect(() => {
        const next = keyword.trim();
        if (next === search) {
            return undefined;
        }
        const timer = setTimeout(() => {
            setSelectedIds(new Set());
            setQuery({...query, page: 0, keyword: next});
        }, SEARCH_DELAY_MS);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [keyword, search]);

    const handleSelectChange = (id: number, selected: boolean) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (selected) {
                next.add(id);
            } else {
                next.delete(id);
            }
            return next;
        });
    };

    const handleMarkSelectedAsRead = async () => {
        const notifications = notificationsData?.data?.content?.filter(
            (n) => selectedIds.has(n.id) && !n.read
        );
        if (!notifications?.length) return;

        store.dispatch(preloaderChangeStatus(true));
        try {
            await Promise.all(
                notifications.map((n) =>
                    markAsRead({
                        notificationId: n.id,
                        data: {
                            businessId: businessId,
                            notificationType: n.type,
                        },
                    }).unwrap()
                )
            );
            NotifySuccess(intl.formatMessage({id: 'notificationsMarkedAsRead'}));
            setSelectedIds(new Set());
        } catch (error) {
            if (isErrorObject(error)) {
                NotifyError(error.data?.message ?? error.data?.error);
            }
        } finally {
            store.dispatch(preloaderChangeStatus(false));
        }
    };

    // One place asks the server, whenever the address bar says something else. Every handler above
    // only writes the URL.
    const filters = useMemo(
        () => ({read: readFlagOf(status), keyword: search || undefined}),
        [status, search]
    );
    useEffect(() => {
        handleGetNotifications(getNotifications, businessId, query.size, query.page, filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [businessId, query.size, query.page, filters]);

    const handlePerPageChange = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        const val = +e.target.value;
        if (typeof val === 'number') {
            setQuery({...query, page: 0, size: val || DEFAULT_PAGE_SIZE});
        }
    };

    const handleChangePage = (e: number) => setQuery({...query, page: e});

    const unreadSelectedCount = notificationsData?.data?.content?.filter(
        (n) => selectedIds.has(n.id) && !n.read
    ).length ?? 0;

    // Marking one read is a write, and the same five roles that may open this page hold it - so
    // this hides nothing from anybody who can be here, and stops offering the action to a role
    // added later that cannot perform it.
    const canMarkAsRead = useCan('notifications.update');
    const isButtonDisabled = selectedIds.size === 0 || unreadSelectedCount === 0;

    const hasItems = !!notificationsData?.data?.content?.length;
    /**
     * Whether the server has actually answered.
     *
     * "Nothing yet" and "nothing at all" are different, and only one of them is worth telling somebody
     * about. Rendering the empty state before the first response meant the page opened by announcing
     * there were no notifications and then filling with them.
     */
    const answered = !!notificationsData
        && !notificationsData.isUninitialized
        && !notificationsData.isFetching;

    // A new page or a new filter starts at the first notification. The list scrolls inside its own box,
    // which the table layout's reset - aimed at a grid - does not reach.
    const listRef = useRef<HTMLDivElement>(null);
    const shownPage = notificationsData?.data?.page?.number;
    useEffect(() => {
        listRef.current?.scrollTo({top: 0});
    }, [shownPage, status, search]);

    return (
        <Box>
            {/* The strip's own heading names the page; a second, larger one above it said the same word
                twice. The top margin clears the app bar, which the page header used to do; the gap below
                is the table card's own, as on the locations page. */}
            <Box sx={{mt: 2.5}}>
                <NotificationStatsStrip
                    data={notificationStats}
                    activeStatus={status}
                    onSelectStatus={handleStatusChange}
                />
            </Box>
            <TableLayout
                // The same toolbar every list has: the list's name and badge with how many matched, then
                // the search and the action on the rows, side by side the way Export sits beside the
                // search on the other tables.
                toolbar={(
                    <FilterBar
                        title={intl.formatMessage({id: 'notifications'})}
                        icon={<NotificationsNoneOutlined/>}
                        subtitle={typeof notificationsData?.data?.page?.totalElements === 'number'
                            ? intl.formatMessage(
                                {id: 'rowsFound'},
                                {count: notificationsData.data.page.totalElements}
                            )
                            : undefined}
                        actions={(
                            <>
                                <OutlinedInput
                                    sx={searchInputSx}
                                    color="secondary"
                                    type="text"
                                    name="search"
                                    value={keyword}
                                    onChange={(event) => setKeyword(event.target.value)}
                                    placeholder={intl.formatMessage({id: 'search'})}
                                    startAdornment={<Search/>}
                                />
                                {canMarkAsRead && (
                                    <Tooltip
                                        title={isButtonDisabled
                                            ? intl.formatMessage({id: 'selectAtLeastOneNotification'})
                                            : ''}
                                        arrow
                                    >
                                        <span>
                                            <Button
                                                sx={toolbarButtonSx}
                                                startIcon={<DoneAllOutlined/>}
                                                onClick={() => void handleMarkSelectedAsRead()}
                                                disabled={isButtonDisabled}
                                            >
                                                <FormattedMessage id="markSelectedAsRead"/> ({unreadSelectedCount})
                                            </Button>
                                        </span>
                                    </Tooltip>
                                )}
                            </>
                        )}
                    />
                )}
                hideFooter={!hasItems}
                totalPages={notificationsData?.data?.page?.totalPages ?? 0}
                page={notificationsData?.data?.page?.number ?? 0}
                pageSize={query.size}
                onPageChange={handleChangePage}
                onPageSizeChange={handlePerPageChange}
            >
                <Stack
                    ref={listRef}
                    sx={{
                        // No side padding of its own below a desktop: the list's own 8px sat on top
                        // of the page's 2px, so these cards were the one list on the product that
                        // stopped short of the edge every other list reaches.
                        px: {xs: 0, md: 1},
                        py: 1,
                        gap: 0.25,
                        // Below a desktop the page scrolls rather than this box: a scroller inside a
                        // scroller is what makes a phone feel like it is fighting back, and `vh`
                        // counts the browser's address bar, so the bottom row sat off-screen.
                        minHeight: {xs: 'unset', md: 'calc(100dvh - 300px)'},
                        maxHeight: {xs: 'none', md: 'calc(100dvh - 300px)'},
                        overflowY: {xs: 'visible', md: 'auto'},
                    }}
                >
                    {answered && !hasItems && <EmptyStatus/>}
                    {hasItems &&
                        notificationsData?.data?.content.map((item) => (
                            <NotificationCard
                                page={query.page}
                                key={item.id}
                                isSelected={selectedIds.has(item.id)}
                                onSelectChange={handleSelectChange}
                                {...item}
                            />
                        ))}
                </Stack>
            </TableLayout>
        </Box>
    );
};
